<script setup>
import { computed, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  currencies: {
    type: Array,
    default: () => [],
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['create']);

const dialogRef = ref(null);
const inboxId = ref('');
const targetCurrency = ref('');
const extendedCreditId = ref('');

const SELECT_CLASS =
  'block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand border-none outline-offset-[-1px] rounded-lg bg-n-alpha-black2 text-n-slate-12';

const isValid = computed(() =>
  Boolean(inboxId.value && targetCurrency.value)
);

const inboxLabel = inbox =>
  `${inbox.name} · ${inbox.phone_number} · WABA ${inbox.business_account_id}`;

const open = () => {
  inboxId.value = props.inboxes.length === 1 ? String(props.inboxes[0].id) : '';
  targetCurrency.value = '';
  extendedCreditId.value = '';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onConfirm = () => {
  if (!isValid.value) return;

  emit('create', {
    inbox_id: Number(inboxId.value),
    target_currency: targetCurrency.value,
    extended_credit_id: extendedCreditId.value.trim(),
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="$t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.TITLE')"
    :description="$t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.DESCRIPTION')"
    :confirm-button-label="
      $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.SUBMIT')
    "
    :disable-confirm-button="!isValid"
    :is-loading="isCreating"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label for="migration-inbox" class="text-sm font-medium text-n-slate-12">
          {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.INBOX') }}
        </label>
        <select
          id="migration-inbox"
          v-model="inboxId"
          :class="SELECT_CLASS"
          data-testid="migration-inbox"
        >
          <option value="" disabled>
            {{
              $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.INBOX_PLACEHOLDER')
            }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="String(inbox.id)">
            {{ inboxLabel(inbox) }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <label for="migration-currency" class="text-sm font-medium text-n-slate-12">
          {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.TARGET_CURRENCY') }}
        </label>
        <select
          id="migration-currency"
          v-model="targetCurrency"
          :class="SELECT_CLASS"
          data-testid="migration-currency"
        >
          <option value="" disabled>
            {{
              $t(
                'CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.CURRENCY_PLACEHOLDER'
              )
            }}
          </option>
          <option v-for="currency in currencies" :key="currency" :value="currency">
            {{ currency }}
          </option>
        </select>
      </div>

      <Input
        id="migration-credit"
        v-model="extendedCreditId"
        :label="
          $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.EXTENDED_CREDIT_ID')
        "
        :message="
          $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DIALOG.EXTENDED_CREDIT_HINT')
        "
        data-testid="migration-credit"
      />
    </div>
  </Dialog>
</template>
